/**
 * voice-v8 — Kontakte-View (Phase E5).
 *
 * Strukturierte Kontakte mit Name/Phone/Email/Firma + Kategorie. Layout
 * orientiert sich am v7-Vorbild: Kategorie-Tabs, Suche, Counter, Cards
 * mit Avatar-Initial + drei Action-Icons (Telegram/WhatsApp/Tel) + grüner
 * Tel-Direkt-Button rechts.
 *
 * Avatar-Farbe wird serverseitig deterministisch aus dem Namen abgeleitet
 * (siehe memory/contacts.ts pickAvatarColor) — dieselbe Logik liegt im
 * Frontend als Fallback (s.u. computeAvatarColor) für Live-Edit-Preview.
 */

import {
  createContact,
  deleteContact,
  listContacts,
  updateContact,
  type ContactCategory,
  type ContactDto,
} from "./contacts-api.js";
import { zeichen as zeichenMarkup } from "./zeichen.js";
import { offenText } from "./oeffnungszeiten.js";
import { buildCloseButton, buildLoadingHint, buildPageHeader, requestGoChat } from "./sheet-ui.js";

interface CategoryDef {
  key: "alle" | ContactCategory;
  label: string;
}

const CATEGORIES: ReadonlyArray<CategoryDef> = [
  { key: "alle", label: "Alle" },
  { key: "familie", label: "Familie" },
  { key: "freunde", label: "Freunde" },
  { key: "arbeit", label: "Arbeit" },
  { key: "dienste", label: "Dienste" },
  { key: "notfall", label: "Notfall" },
  { key: "allgemein", label: "Allgemein" },
];

/**
 * Ein Zeichen je Kategorie — Martins "vielleicht sogar mit einem Symbol-Foto".
 *
 * 2026-08-24: Auf der Seite standen vier Kontakte untereinander, und alle vier
 * Kreise sahen gleich aus: dasselbe Orange, dieselbe Initiale "A" (Albert,
 * Antiker Ziegel, Aoede). Die Farbe wird aus dem Namen gerechnet — bei
 * gleichem Anfangsbuchstaben trennt sie nichts.
 *
 * Ein Zeichen sagt auf einen Blick, WORUM es geht: Arzt oder Bruder, Werkstatt
 * oder Freund. Das ist mehr Unterscheidung als jede Farbe. Ein echtes Foto
 * waere schoener, braucht aber Upload und Speicher — das Zeichen kostet nichts
 * und wirkt sofort.
 */
/**
 * 2026-08-26: Hier standen Emoji (Haus, Handschlag, Aktentasche,
 * Schraubenschluessel, Rettungswagen). Sie waren mein eigener Vorschlag vom
 * 24.08. und im Design-Befund vom 25.08. habe ich sie selbst verworfen:
 * Emoji sehen auf jedem Geraet anders aus, tragen eine fremde Farbigkeit ins
 * warme Papier und lassen sich nicht einfaerben.
 *
 * Jetzt aus demselben Vorrat wie die Zeilen auf Zuhause und die Fusszeile.
 */
const KATEGORIE_ZEICHEN: Record<string, string> = {
  familie: "familie",
  freunde: "freunde",
  arbeit: "arbeit",
  dienste: "dienste",
  notfall: "notfall",
  allgemein: "",
};

const PAGE_SIZE = 10;


function getInitial(name: string): string {
  const trimmed = name.trim();
  if (!trimmed) return "?";
  // Erstes echtes Buchstaben-Zeichen — Unicode-aware (\p{L}).
  const match = trimmed.match(/\p{L}/u);
  return (match ? match[0] : trimmed.charAt(0)).toUpperCase();
}

/** Strip leading 0/+ and spaces — for wa.me / t.me schemes. */
function digitsOnly(phone: string): string {
  return phone.replace(/[^\d]/g, "").replace(/^0+/, "");
}

function normalizeTelHref(phone: string): string {
  const trimmed = phone.trim();
  if (!trimmed) return "";
  // tel: erlaubt + und Ziffern. Behalte +, sonst nur Ziffern.
  const stripped = trimmed.replace(/[^\d+]/g, "");
  return stripped ? `tel:${stripped}` : "";
}

function whatsappHref(phone: string): string {
  const d = digitsOnly(phone);
  return d ? `https://wa.me/${d}` : "";
}

function telegramHref(phone: string): string {
  const d = digitsOnly(phone);
  return d ? `https://t.me/+${d}` : "";
}

const NS = "http://www.w3.org/2000/svg";
function makeSvg(viewBox: string, pathD: string, size = 18): SVGSVGElement {
  const svg = document.createElementNS(NS, "svg");
  svg.setAttribute("viewBox", viewBox);
  svg.setAttribute("width", String(size));
  svg.setAttribute("height", String(size));
  svg.setAttribute("aria-hidden", "true");
  const path = document.createElementNS(NS, "path");
  path.setAttribute("fill", "currentColor");
  path.setAttribute("d", pathD);
  svg.appendChild(path);
  return svg;
}

const PHONE_PATH =
  "M6.62 10.79a15.05 15.05 0 0 0 6.59 6.59l2.2-2.2a1 1 0 0 1 1.05-.24c1.16.39 2.4.6 3.69.6a1 1 0 0 1 1 1V20a1 1 0 0 1-1 1A17 17 0 0 1 3 4a1 1 0 0 1 1-1h3.5a1 1 0 0 1 1 1c0 1.29.21 2.53.6 3.69a1 1 0 0 1-.25 1.05l-2.23 2.05z";
const WHATSAPP_PATH =
  "M20.52 3.48A11.93 11.93 0 0 0 12 0C5.37 0 0 5.37 0 12a11.93 11.93 0 0 0 1.64 6.05L0 24l6.07-1.59A12 12 0 0 0 12 24c6.63 0 12-5.37 12-12 0-3.2-1.25-6.21-3.48-8.52zM12 21.82a9.79 9.79 0 0 1-5-1.37l-.36-.21-3.6.94.96-3.5-.23-.36A9.81 9.81 0 1 1 12 21.82zm5.45-7.36c-.3-.15-1.77-.87-2.04-.97-.27-.1-.47-.15-.67.15-.2.3-.77.97-.94 1.17-.17.2-.34.22-.64.07-.3-.15-1.27-.47-2.42-1.5-.9-.8-1.5-1.78-1.67-2.08-.18-.3-.02-.46.13-.6.13-.13.3-.34.45-.5.15-.17.2-.3.3-.5.1-.2.05-.37-.02-.52-.07-.15-.67-1.62-.92-2.21-.24-.59-.49-.5-.67-.51l-.57-.01c-.2 0-.52.07-.79.37-.27.3-1.03 1.01-1.03 2.46s1.06 2.86 1.21 3.06c.15.2 2.08 3.18 5.04 4.46.7.3 1.25.48 1.68.62.7.22 1.34.19 1.85.12.56-.08 1.77-.72 2.02-1.42.25-.7.25-1.3.17-1.42-.08-.13-.27-.2-.57-.35z";
const TELEGRAM_PATH =
  "M12 0a12 12 0 1 0 0 24 12 12 0 0 0 0-24zm5.55 8.16-1.86 8.78c-.14.62-.5.77-1.02.48l-2.82-2.08-1.36 1.31c-.15.15-.28.28-.57.28l.2-2.88 5.24-4.74c.23-.2-.05-.32-.35-.12L8.5 12.27 5.7 11.4c-.6-.19-.62-.6.13-.89l10.95-4.22c.5-.18.94.12.77.87z";
// Pre.139: Stroke-Path durch Fill-Path ersetzt — makeSvg setzt fill,
// kein stroke; das alte "M12 5v14m-7-7h14" war damit unsichtbar.
const PLUS_PATH = "M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6v2z";
const PENCIL_PATH =
  "M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zM20.71 7.04a1 1 0 0 0 0-1.41l-2.34-2.34a1 1 0 0 0-1.41 0l-1.83 1.83 3.75 3.75 1.83-1.83z";
const TRASH_PATH =
  "M6 19a2 2 0 0 0 2 2h8a2 2 0 0 0 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z";

export class ContactsView {
  private root: HTMLElement;
  private body!: HTMLElement;
  private categoryBar!: HTMLElement;
  private searchInput!: HTMLInputElement;
  private counter!: HTMLElement;
  private items: ContactDto[] = [];
  private total = 0;
  // Pre.886 (#144): Anzahl je Kategorie für die Tab-Zähler.
  private categoryCounts: Record<string, number> = {};
  private query = "";
  private selectedCategory: "alle" | ContactCategory = "alle";
  private displayedCount = PAGE_SIZE;

  constructor(root: HTMLElement) {
    this.root = root;
    while (root.firstChild) root.removeChild(root.firstChild);
    root.classList.add("contacts-view");
    this.renderShell();
    void this.refresh();
  }

  async refresh(): Promise<void> {
    // 2026-07-06 (Martin): waehrend geladen wird, sichtbaren Hinweis zeigen
    // (nur wenn noch nichts da ist -> kein Flackern beim Re-Refresh).
    if (!this.body.hasChildNodes()) {
      this.body.replaceChildren(buildLoadingHint("Lade Kontakte \u2026"));
    }
    try {
      const data = await listContacts({
        category: this.selectedCategory,
      });
      this.items = data.items;
      this.total = data.total;
      this.categoryCounts = data.categoryCounts;
      this.displayedCount = PAGE_SIZE;
      this.renderBody();
      this.updateCounter();
      this.updateCategoryCounts();
    } catch (err) {
      this.items = [];
      this.total = 0;
      this.renderBody(
        `Konnte Kontakte nicht laden: ${
          err instanceof Error ? err.message : String(err)
        }`,
      );
    }
  }

  // --- Shell --------------------------------------------------------------

  private renderShell(): void {
    const header = document.createElement("header");
    header.className = "contacts-header";

    // NORM 2026-07-21: einheitlicher Kopf (Pfeil -> Chat, 'Neu' als
    // sichtbare Aktion); Suche/Chips/Zaehler bleiben darunter.
    const newBtn = document.createElement("button");
    newBtn.type = "button";
    newBtn.className = "contacts-new-btn";
    newBtn.appendChild(makeSvg("0 0 24 24", PLUS_PATH, 16));
    const newLabel = document.createElement("span");
    newLabel.textContent = "Neu";
    newBtn.appendChild(newLabel);
    newBtn.addEventListener("click", () => this.openCreateSheet());

    header.appendChild(
      buildPageHeader("Kontakte", () => requestGoChat(), {
        reopenMenu: false,
        action: newBtn,
      }),
    );

    const searchWrap = document.createElement("div");
    searchWrap.className = "contacts-search";
    this.searchInput = document.createElement("input");
    this.searchInput.type = "search";
    this.searchInput.className = "contacts-search-input";
    this.searchInput.placeholder = "Suche nach Name, Telefon, Firma…";
    this.searchInput.addEventListener("input", () => {
      this.query = this.searchInput.value.trim().toLowerCase();
      this.displayedCount = PAGE_SIZE;
      this.renderBody();
      this.updateCounter();
    });
    searchWrap.appendChild(this.searchInput);
    header.appendChild(searchWrap);

    this.categoryBar = document.createElement("div");
    this.categoryBar.className = "contacts-categories";
    for (const cat of CATEGORIES) {
      const btn = document.createElement("button");
      btn.type = "button";
      btn.className = "contacts-category-chip";
      btn.dataset.category = cat.key;
      btn.textContent = cat.label;
      if (cat.key === this.selectedCategory) btn.classList.add("is-active");
      btn.addEventListener("click", () => {
        if (this.selectedCategory === cat.key) return;
        this.selectedCategory = cat.key;
        for (const c of this.categoryBar.querySelectorAll(
          ".contacts-category-chip",
        )) {
          c.classList.toggle(
            "is-active",
            (c as HTMLElement).dataset.category === cat.key,
          );
        }
        void this.refresh();
      });
      this.categoryBar.appendChild(btn);
    }
    header.appendChild(this.categoryBar);

    this.counter = document.createElement("div");
    this.counter.className = "contacts-counter";
    this.counter.textContent = "0 Kontakte";
    header.appendChild(this.counter);

    this.root.appendChild(header);

    this.body = document.createElement("div");
    this.body.className = "contacts-body";
    this.root.appendChild(this.body);
  }

  // --- Body --------------------------------------------------------------

  private filteredItems(): ContactDto[] {
    if (!this.query) return this.items;
    const q = this.query;
    return this.items.filter((c) => {
      const hay = [c.name, c.phone ?? "", c.company ?? "", c.email ?? ""]
        .join(" ")
        .toLowerCase();
      return hay.includes(q);
    });
  }

  /**
   * Pre.886 (#144): Schreibt die Anzahl je Kategorie in die Tab-Beschriftung,
   * z.B. „Familie (5)". „Alle" zeigt die Gesamtzahl. Leere Kategorien bleiben
   * ohne Klammer (kein „(0)").
   */
  private updateCategoryCounts(): void {
    if (!this.categoryBar) return;
    for (const cat of CATEGORIES) {
      const btn = this.categoryBar.querySelector<HTMLButtonElement>(
        `.contacts-category-chip[data-category="${cat.key}"]`,
      );
      if (!btn) continue;
      const n =
        cat.key === "alle" ? this.total : (this.categoryCounts[cat.key] ?? 0);
      btn.textContent = n > 0 ? `${cat.label} (${n})` : cat.label;
    }
  }

  private updateCounter(): void {
    const filtered = this.filteredItems();
    const shown = Math.min(this.displayedCount, filtered.length);
    if (filtered.length === this.total && !this.query) {
      this.counter.textContent = `${shown} von ${this.total} Kontakten`;
    } else {
      this.counter.textContent = `${shown} von ${filtered.length} (Gesamt ${this.total})`;
    }
  }

  private renderBody(errorMsg?: string): void {
    while (this.body.firstChild) this.body.removeChild(this.body.firstChild);
    if (errorMsg) {
      this.body.appendChild(emptyEl(errorMsg));
      return;
    }
    if (this.items.length === 0) {
      this.body.appendChild(
        emptyEl(
          this.selectedCategory === "alle"
            ? 'Noch keine Kontakte. Leg einen an mit "+ Neu" oder sag dem Bot z.B. "Speicher Mama mit der Nummer 0664 123 als Kontakt".'
            : "Keine Kontakte in dieser Kategorie.",
        ),
      );
      return;
    }
    const filtered = this.filteredItems();
    if (filtered.length === 0) {
      this.body.appendChild(emptyEl(`Keine Treffer für „${this.query}".`));
      return;
    }
    const visible = filtered.slice(0, this.displayedCount);
    const list = document.createElement("ul");
    list.className = "contacts-list";
    for (const item of visible) list.appendChild(this.renderCard(item));
    this.body.appendChild(list);

    if (filtered.length > visible.length) {
      const more = document.createElement("button");
      more.type = "button";
      more.className = "contacts-more-btn";
      more.textContent = `Weitere ${Math.min(
        PAGE_SIZE,
        filtered.length - visible.length,
      )} laden`;
      more.addEventListener("click", () => {
        this.displayedCount += PAGE_SIZE;
        this.renderBody();
        this.updateCounter();
      });
      this.body.appendChild(more);
    }
  }

  // --- Card --------------------------------------------------------------

  private renderCard(item: ContactDto): HTMLElement {
    const li = document.createElement("li");
    li.className = "contact-card";
    li.dataset.id = item.id;

    // Pre.142: Card-Layout neu strukturiert. Header (Avatar + Main +
    // Tel-Btn) bleibt eine stabile Reihe in vertikaler Mitte. Details-
    // Block hängt darunter und füllt die VOLLE Card-Breite.
    const headerRow = document.createElement("div");
    headerRow.className = "contact-card-header";

    // Avatar (Initial in farbigem Kreis).
    const avatar = document.createElement("div");
    avatar.className = "contact-avatar";
    // 2026-08-26 (Norm A + C, Martin: "endlich vereinheitlichen"):
    //
    // Hier stand eine Farbe, die aus einem Hash des NAMENS kam — acht harte
    // Hex-Werte, die jede Palette ignorierten. Im Gruen sahen vier von fuenf
    // Kreisen gleich orange-rot aus und einer magenta: die Farbe unterschied
    // also nichts und stach zugleich aus dem Papier heraus.
    //
    // Was den Kontakt einordnet, steht ohnehin IM Kreis — das gezeichnete
    // Zeichen fuer Familie, Freunde, Arbeit, Dienste, Notfall. Der Kreis
    // traegt jetzt nur noch einen ruhigen Ton der jeweiligen Palette; die
    // Farbe kommt aus dem Stylesheet, nicht aus dem Namen.
    //
    // avatarColor vom Server wird bewusst NICHT mehr gelesen: es sind
    // dieselben acht Hex-Werte.
    // Kategorie-Zeichen schlaegt die Initiale: Bei "Albert", "Antiker Ziegel"
    // und "Aoede" untereinander sagt ein drittes "A" nichts mehr.
    const zeichen = KATEGORIE_ZEICHEN[item.category ?? ""] ?? "";
    if (zeichen) {
      avatar.innerHTML = zeichenMarkup(zeichen, 20);
      avatar.classList.add("contact-avatar--zeichen");
    } else {
      avatar.textContent = getInitial(item.name);
    }
    headerRow.appendChild(avatar);

    const main = document.createElement("div");
    main.className = "contact-main";

    const name = document.createElement("h3");
    name.className = "contact-name";
    name.textContent = item.name;
    main.appendChild(name);

    if (item.phone) {
      const phoneRow = document.createElement("div");
      phoneRow.className = "contact-phone";
      phoneRow.textContent = item.phone;
      main.appendChild(phoneRow);
    }

    /*
     * 2026-08-24 (Martin: "Bitte schau mal nach, wann heute der Arzt in
     * Hollenthon wieder öffnet." — und spaeter: "die Kontaktseite überarbeiten
     * und erweitern mit den Zusatzinformationen wie Öffnungszeiten"):
     *
     *   Die Zeiten standen schon da — aber zugeklappt unter "Details", als
     *   Rohtext ("Mo 07:30-11:00, 13:00-16:00"). Selbst aufgeklappt musste man
     *   sie im Kopf mit der Uhr vergleichen.
     *
     *   Die Frage ist nie "wie lauten die Öffnungszeiten", sondern "kann ich
     *   jetzt hin?". Genau das steht jetzt sichtbar neben dem Namen.
     *
     *   offenText() gibt null zurueck, wenn es die Schreibweise nicht sicher
     *   versteht — dann bleibt es beim Rohtext in den Details. Ein falsches
     *   "jetzt offen" waere schlimmer als keine Auskunft: Danach steht Martin
     *   vor einer verschlossenen Tuer.
     */
    const jetztOffen = offenText(item.openingHours);
    if (jetztOffen) {
      const stand = document.createElement("div");
      stand.className = "contact-offen";
      if (jetztOffen.startsWith("Jetzt offen")) {
        stand.classList.add("contact-offen--jetzt");
      }
      stand.textContent = jetztOffen;
      main.appendChild(stand);
    }

    // Pre.139: Card kompakt — Email/Company/Actions/Website/Hours/Notes
    // wandern alle in einen ausklappbaren Details-Block. Default sichtbar
    // nur Avatar + Name + Phone + großer Tel-Btn.
    const details = document.createElement("div");
    details.className = "contact-details";
    details.hidden = true;

    if (item.email) {
      const emailRow = document.createElement("div");
      emailRow.className = "contact-email";
      const a = document.createElement("a");
      a.href = `mailto:${item.email}`;
      a.textContent = item.email;
      emailRow.appendChild(a);
      details.appendChild(emailRow);
    }

    if (item.company) {
      const companyRow = document.createElement("div");
      companyRow.className = "contact-company";
      companyRow.textContent = item.company;
      details.appendChild(companyRow);
    }

    if (item.website) {
      const row = document.createElement("div");
      row.className = "contact-detail-row";
      const label = document.createElement("span");
      label.className = "contact-detail-label";
      label.textContent = "Website";
      const a = document.createElement("a");
      a.href = item.website.startsWith("http")
        ? item.website
        : `https://${item.website}`;
      a.target = "_blank";
      a.rel = "noopener noreferrer";
      a.className = "contact-detail-value";
      a.textContent = item.website.replace(/^https?:\/\//, "");
      row.appendChild(label);
      row.appendChild(a);
      details.appendChild(row);
    }

    if (item.openingHours) {
      const row = document.createElement("div");
      row.className = "contact-detail-row";
      const label = document.createElement("span");
      label.className = "contact-detail-label";
      label.textContent = "Öffnungszeiten";
      const v = document.createElement("span");
      v.className = "contact-detail-value";
      v.textContent = item.openingHours;
      row.appendChild(label);
      row.appendChild(v);
      details.appendChild(row);
    }

    if (item.notes) {
      const row = document.createElement("div");
      row.className = "contact-detail-row";
      const label = document.createElement("span");
      label.className = "contact-detail-label";
      label.textContent = "Notizen";
      const v = document.createElement("span");
      v.className = "contact-detail-value";
      v.textContent = item.notes;
      row.appendChild(label);
      row.appendChild(v);
      details.appendChild(row);
    }

    // Action-Buttons in den Details-Block.
    const actions = document.createElement("div");
    actions.className = "contact-actions";

    if (item.phone) {
      const tgHref = telegramHref(item.phone);
      if (tgHref) {
        const tg = document.createElement("a");
        tg.className = "contact-action contact-action-tg";
        tg.href = tgHref;
        tg.target = "_blank";
        tg.rel = "noopener noreferrer";
        tg.title = "Telegram";
        tg.setAttribute("aria-label", "Telegram öffnen");
        tg.appendChild(makeSvg("0 0 24 24", TELEGRAM_PATH, 16));
        actions.appendChild(tg);
      }

      const waHref = whatsappHref(item.phone);
      if (waHref) {
        const wa = document.createElement("a");
        wa.className = "contact-action contact-action-wa";
        wa.href = waHref;
        wa.target = "_blank";
        wa.rel = "noopener noreferrer";
        wa.title = "WhatsApp";
        wa.setAttribute("aria-label", "WhatsApp öffnen");
        wa.appendChild(makeSvg("0 0 24 24", WHATSAPP_PATH, 16));
        actions.appendChild(wa);
      }
    }

    const editBtn = document.createElement("button");
    editBtn.type = "button";
    editBtn.className = "contact-action contact-action-edit";
    editBtn.title = "Bearbeiten";
    editBtn.setAttribute("aria-label", "Kontakt bearbeiten");
    editBtn.appendChild(makeSvg("0 0 24 24", PENCIL_PATH, 16));
    editBtn.addEventListener("click", () => this.openEditSheet(item));
    actions.appendChild(editBtn);

    const delBtn = document.createElement("button");
    delBtn.type = "button";
    delBtn.className = "contact-action contact-action-delete";
    delBtn.title = "Löschen";
    delBtn.setAttribute("aria-label", "Kontakt löschen");
    delBtn.appendChild(makeSvg("0 0 24 24", TRASH_PATH, 16));
    delBtn.addEventListener("click", () => void this.handleDelete(item));
    actions.appendChild(delBtn);

    details.appendChild(actions);

    // Toggle-Btn — immer sichtbar damit User Edit/Delete erreichen kann.
    const toggle = document.createElement("button");
    toggle.type = "button";
    /*
     * 2026-08-24: Hier stand "Details ▾" als eigene Textzeile unter jedem
     * Kontakt — bei zwanzig Kontakten zwanzig Mal dasselbe Wort, das nichts
     * ueber den Kontakt sagt. Vier Kontakte fuellten damit einen ganzen
     * Bildschirm.
     *
     * Jetzt ein schmaler Pfeil rechts am Rand: dieselbe Funktion, kein Platz.
     * Der Name bleibt oben, wo das Auge ihn sucht.
     */
    toggle.className = "contact-details-toggle";
    // 2026-08-26 (Norm A): war ▾ (U+25BE) — ein Dingbat neben lauter
    // gezeichneten Zeichen.
    toggle.innerHTML = zeichenMarkup("pfeil-ab", 18);
    toggle.setAttribute("aria-label", "Details anzeigen");
    toggle.setAttribute("aria-expanded", "false");
    toggle.addEventListener("click", () => {
      const open = details.hidden;
      details.hidden = !open;
      toggle.innerHTML = zeichenMarkup(open ? "pfeil-auf" : "pfeil-ab", 18);
      toggle.setAttribute("aria-label", open ? "Details verbergen" : "Details anzeigen");
      toggle.setAttribute("aria-expanded", String(open));
    });
    headerRow.appendChild(main);

    // Großer grüner Phone-Direkt-Button rechts (im Header-Row).
    //
    // 2026-08-26: Der Anruf-Knopf haengt hier VOR dem Aufklapper, nicht mehr
    // danach. Vorher stand der Aufklapper fest an zweiter Stelle und rutschte
    // um die Breite des Anruf-Knopfes nach rechts, sobald ein Kontakt keine
    // Nummer hat — gemessen sprang er von Zeile zu Zeile um rund 55px. Die
    // rechte Spalte war dadurch keine Spalte.
    if (item.phone) {
      const tHref = normalizeTelHref(item.phone);
      if (tHref) {
        const big = document.createElement("a");
        big.className = "contact-call-btn";
        big.href = tHref;
        big.title = "Direkt anrufen";
        big.setAttribute("aria-label", `${item.name} anrufen`);
        big.appendChild(makeSvg("0 0 24 24", PHONE_PATH, 22));
        headerRow.appendChild(big);
      }
    }

    // Der Aufklapper steht IMMER ganz rechts — er ist die einzige Aktion, die
    // jede Zeile hat, und darf deshalb als einzige eine feste Spalte bilden.
    headerRow.appendChild(toggle);

    li.appendChild(headerRow);
    // Details füllt die volle Card-Breite, hängt UNTER dem Header.
    li.appendChild(details);

    return li;
  }

  // --- Actions ----------------------------------------------------------

  private async handleDelete(item: ContactDto): Promise<void> {
    const { confirmSheet } = await import("./sheet-ui.js");
    const ok = await confirmSheet({
      title: `Kontakt „${item.name}" löschen?`,
      confirmLabel: "Löschen",
      danger: true,
    });
    if (!ok) return;
    try {
      await deleteContact(item.id);
      this.items = this.items.filter((c) => c.id !== item.id);
      this.total = Math.max(0, this.total - 1);
      this.renderBody();
      this.updateCounter();
    } catch (err) {
      window.alert(
        `Löschen fehlgeschlagen: ${
          err instanceof Error ? err.message : String(err)
        }`,
      );
    }
  }

  private openCreateSheet(): void {
    openContactSheet({
      title: "Neuer Kontakt",
      submit: "Anlegen",
      initial: {
        id: "",
        userId: 0,
        name: "",
        phone: null,
        email: null,
        company: null,
        category: "allgemein",
        notes: null,
        avatarColor: null,
        website: null,
        openingHours: null,
        createdAt: "",
        updatedAt: null,
      },
      onSubmit: async (input) => {
        try {
          await createContact({
            name: input.name,
            phone: input.phone || null,
            email: input.email || null,
            company: input.company || null,
            website: input.website || null,
            openingHours: input.openingHours || null,
            category: input.category,
            notes: input.notes || null,
          });
          await this.refresh();
        } catch (err) {
          window.alert(
            `Anlegen fehlgeschlagen: ${
              err instanceof Error ? err.message : String(err)
            }`,
          );
        }
      },
    });
  }

  private openEditSheet(item: ContactDto): void {
    openContactSheet({
      title: "Kontakt bearbeiten",
      submit: "Speichern",
      initial: item,
      onSubmit: async (input) => {
        try {
          await updateContact(item.id, {
            name: input.name,
            phone: input.phone || null,
            email: input.email || null,
            company: input.company || null,
            website: input.website || null,
            openingHours: input.openingHours || null,
            category: input.category,
            notes: input.notes || null,
          });
          await this.refresh();
        } catch (err) {
          window.alert(
            `Speichern fehlgeschlagen: ${
              err instanceof Error ? err.message : String(err)
            }`,
          );
        }
      },
    });
  }
}

// --- Sheet (Create/Edit) ------------------------------------------------

interface SheetInput {
  name: string;
  phone: string;
  email: string;
  company: string;
  website: string;
  openingHours: string;
  category: ContactCategory;
  notes: string;
}

function openContactSheet(opts: {
  title: string;
  submit: string;
  initial: ContactDto;
  onSubmit: (input: SheetInput) => Promise<void> | void;
}): void {
  const overlay = document.createElement("div");
  overlay.className = "contact-sheet-overlay";
  overlay.addEventListener("click", (ev) => {
    if (ev.target === overlay) overlay.remove();
  });

  const sheet = document.createElement("div");
  sheet.className = "contact-sheet";
  sheet.setAttribute("role", "dialog");
  sheet.setAttribute("aria-modal", "true");

  const close = buildCloseButton(() => overlay.remove());
  close.classList.add("close-btn--corner");
  sheet.appendChild(close);

  const titleEl = document.createElement("h2");
  titleEl.className = "contact-sheet-title";
  titleEl.textContent = opts.title;
  sheet.appendChild(titleEl);

  const nameInput = mkInput("Name *", "Vorname Nachname", opts.initial.name);
  const phoneInput = mkInput(
    "Telefon",
    "z.B. 0664 1234567",
    opts.initial.phone ?? "",
  );
  const emailInput = mkInput(
    "E-Mail",
    "name@beispiel.com",
    opts.initial.email ?? "",
  );
  const companyInput = mkInput(
    "Firma",
    "Firma oder Organisation",
    opts.initial.company ?? "",
  );
  // Pre.136: Website + Öffnungszeiten als zusätzliche Felder. Bot füllt
  // sie bei Geschäfts-Kontakten via web_search automatisch.
  const websiteInput = mkInput(
    "Website",
    "https://...",
    opts.initial.website ?? "",
  );
  const openingHoursInput = mkInput(
    "Öffnungszeiten",
    "Mo-Fr 8-17 Uhr",
    opts.initial.openingHours ?? "",
  );

  const categoryRow = document.createElement("div");
  categoryRow.className = "contact-sheet-field";
  const categoryLabel = document.createElement("label");
  categoryLabel.textContent = "Kategorie";
  categoryLabel.className = "contact-sheet-label";
  categoryRow.appendChild(categoryLabel);
  const categorySelect = document.createElement("select");
  categorySelect.className = "contact-sheet-select";
  for (const c of CATEGORIES.filter((c) => c.key !== "alle")) {
    const opt = document.createElement("option");
    opt.value = c.key;
    opt.textContent = c.label;
    if (c.key === opts.initial.category) opt.selected = true;
    categorySelect.appendChild(opt);
  }
  categoryRow.appendChild(categorySelect);

  const notesRow = document.createElement("div");
  notesRow.className = "contact-sheet-field";
  const notesLabel = document.createElement("label");
  notesLabel.textContent = "Notiz";
  notesLabel.className = "contact-sheet-label";
  notesRow.appendChild(notesLabel);
  const notesInput = document.createElement("textarea");
  notesInput.className = "contact-sheet-textarea";
  notesInput.rows = 2;
  notesInput.maxLength = 4000;
  notesInput.placeholder = "Optional";
  notesInput.value = opts.initial.notes ?? "";
  notesRow.appendChild(notesInput);

  sheet.appendChild(nameInput.field);
  sheet.appendChild(phoneInput.field);
  sheet.appendChild(emailInput.field);
  sheet.appendChild(companyInput.field);
  sheet.appendChild(websiteInput.field);
  sheet.appendChild(openingHoursInput.field);
  sheet.appendChild(categoryRow);
  sheet.appendChild(notesRow);

  const actions = document.createElement("div");
  actions.className = "contact-sheet-actions";
  const cancel = document.createElement("button");
  cancel.type = "button";
  cancel.className = "contact-sheet-btn";
  cancel.textContent = "Abbrechen";
  cancel.addEventListener("click", () => overlay.remove());
  const submit = document.createElement("button");
  submit.type = "button";
  submit.className = "contact-sheet-btn contact-sheet-btn-primary";
  submit.textContent = opts.submit;
  submit.addEventListener("click", () => {
    const name = nameInput.input.value.trim();
    if (!name) {
      nameInput.input.focus();
      return;
    }
    overlay.remove();
    void opts.onSubmit({
      name,
      phone: phoneInput.input.value.trim(),
      email: emailInput.input.value.trim(),
      company: companyInput.input.value.trim(),
      website: websiteInput.input.value.trim(),
      openingHours: openingHoursInput.input.value.trim(),
      category: categorySelect.value as ContactCategory,
      notes: notesInput.value.trim(),
    });
  });
  actions.appendChild(cancel);
  actions.appendChild(submit);
  sheet.appendChild(actions);

  overlay.appendChild(sheet);
  document.body.appendChild(overlay);
  requestAnimationFrame(() => nameInput.input.focus());
}

function mkInput(
  label: string,
  placeholder: string,
  value: string,
): { field: HTMLElement; input: HTMLInputElement } {
  const field = document.createElement("div");
  field.className = "contact-sheet-field";
  const lab = document.createElement("label");
  lab.className = "contact-sheet-label";
  lab.textContent = label;
  field.appendChild(lab);
  const input = document.createElement("input");
  input.type = "text";
  input.className = "contact-sheet-input";
  input.placeholder = placeholder;
  input.value = value;
  field.appendChild(input);
  return { field, input };
}

function emptyEl(msg: string): HTMLElement {
  const el = document.createElement("div");
  el.className = "contacts-empty";
  el.textContent = msg;
  return el;
}
